import { useTranslation } from "react-i18next";
import { createRef, forwardRef, useState, useEffect } from "react";
import { Select, MenuItem, FormControl, TextField, Popover, Checkbox } from "@mui/material";
import ButtonComponent from "../../form/ButtonComponent";
import Autocomplete from "@mui/material/Autocomplete";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import axiosWrapper from "../../../services/axiosWrapper";
import get from "lodash/get";
import PropTypes from "prop-types";

const FILTER_MAPPER = {
  Text: [
    "Starts With",
    "Ends With",
    "Empty",
    "Equal",
    "Contains",
    "Does Not Start With",
    "Does Not End With",
    "Does Not Contain",
    "Not Equal",
    "Not Empty",
  ],
  Date: [
    "Equal",
    "Greater Than",
    "Greater Than Or Equal",
    "Less Than",
    "Less Than Or Equal",
    "Between",
    "Not Equal",
    "Null",
    "Not Null",
  ],
  Number: [
    "Equal",
    "Greater Than",
    "Greater Than Or Equal",
    "Less Than",
    "Less Than Or Equal",
    "Between",
    "Not Equal",
    "Null",
    "Not Null",
  ],
  MultiSelect: ["Equal"],
};

const TEXT_FIELD_DISABLED_OPTIONS = ["empty", "null", "notnull", "notempty"];

const DEFAULT_FILTER_OPTIONS = {
  Text: { option: "startswith", text: "" },
  Date: { option: "equal", text: { date: dayjs() } },
  Number: { option: "equal", text: 0 },
  MultiSelect: { option: "equal", text: [] },
};

const FilterComponent = forwardRef(
  (
    {
      currentColumnId,
      setCurrentColumnId,
      setFilterPredicates,
      anchorEl,
      setAnchorEl,
      filterType,
      filterOpened,
      setFilterOpened,
      remoteDataSource,
      beforeDataBound,
      columnDefinitions,
      setAppliedFilters,
      datasetForFetchingOptions,
      query,
      localData,
      appliedFilters,
      filterConditions,
      setFilterConditions,
      getUpdatedFilterConditions,
      applyFilter,
      preAppliedFilters,
      onFilterConditionsChange,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const gridRef = ref || createRef(null);
    const [tempFilterOption, setTempFilterOption] = useState("");
    const [tempFilterText, setTempFilterText] = useState("");
    const [options, setOptions] = useState([]);
    const [multiSelectFilteredOptions, setMultiSelectFilteredOptions] = useState([]);
    const [multiSelectSearchValue, setMultiSelectSearchValue] = useState("");
    const [loading, setLoading] = useState(false);
    const [optionsOpen, setOptionsOpen] = useState(false);
    const openPopover = Boolean(anchorEl);
    const popoverId = openPopover ? "filter-popover" : undefined;

    useEffect(() => {
      if (filterOpened) {
        if (appliedFilters.filter((data) => data === currentColumnId).length) {
          const filterCondition = filterConditions[currentColumnId];
          if (filterCondition) {
            setTempFilterText(filterCondition.text);
            setTempFilterOption(filterCondition.option || DEFAULT_FILTER_OPTIONS[filterType].option);
          }
        } else if (filterType) {
          setTempFilterOption(DEFAULT_FILTER_OPTIONS[filterType].option);
          setTempFilterText(DEFAULT_FILTER_OPTIONS[filterType].text);
        }
      }
      setFilterOpened(false);
    }, [filterOpened]);

    useEffect(() => {
      if (currentColumnId) {
        if (!filterConditions[currentColumnId]) {
          setFilterConditions((prev) => ({
            ...prev,
            [currentColumnId]: {
              ...prev[currentColumnId],
              id: currentColumnId,
              type: filterType,
              predicate: [
                {
                  field: currentColumnId,
                  operator: DEFAULT_FILTER_OPTIONS[filterType].option,
                  value: DEFAULT_FILTER_OPTIONS[filterType].text,
                },
              ],
            },
          }));
        }
      }
    }, [currentColumnId]);

    useEffect(() => {
      handlePreAppliedFilter();
    }, [preAppliedFilters]);

    const handlePreAppliedFilter = () => {
      if (preAppliedFilters) {
        const predicateUpdatedFilter = Object.keys(preAppliedFilters).reduce((acc, filterKey) => {
          acc[filterKey] = {
            ...structuredClone(preAppliedFilters[filterKey]),
            predicate: getTempPredicateArray(
              preAppliedFilters[filterKey].text,
              preAppliedFilters[filterKey].option,
              preAppliedFilters[filterKey].filterType,
              filterKey,
            ),
          };
          return acc;
        }, {});
        setFilterConditions(structuredClone(predicateUpdatedFilter));
        getFilterPredicates(predicateUpdatedFilter, true);
        setAppliedFilters(Object.keys(preAppliedFilters));
      }
    };

    useEffect(() => {
      if (tempFilterOption == "equal" || filterType === "MultiSelect") {
        fetchOptions("", currentColumnId, "startswith");
      }
      if (optionsOpen && (filterType == "Text" || filterType == "Number") && tempFilterOption != "equal") {
        fetchOptions(tempFilterText, currentColumnId, tempFilterOption);
      }
    }, [optionsOpen, tempFilterText, tempFilterOption]);
    const handleCloseOptions = () => setOptionsOpen(false);

    const handleClosePopover = () => {
      setTempFilterText("");
      setTempFilterOption("");
      setMultiSelectSearchValue("");
      setMultiSelectFilteredOptions([]);
      if (!appliedFilters.filter((data) => data === currentColumnId).length) {
        setFilterConditions((prev) => {
          const newState = { ...prev };
          delete newState[currentColumnId];
          return newState;
        });
      }
      setAnchorEl(null);
      setOptionsOpen(false);
    };

    const getFilterPredicates = (updatedFilterConditions, callForData) => {
      let predicates = [];
      let whereClause = [];
      if (Object.keys(updatedFilterConditions).length == 0 && callForData) {
        setFilterPredicates("");
      }

      Object.keys(updatedFilterConditions).forEach((filterkey) => {
        const filterCondition = updatedFilterConditions[filterkey];
        const enhancedPredicates = filterCondition.predicate.map((predicate) => ({
          ...predicate,
          isComplex: false,
          ignoreCase: true,
          ignoreAccent: false,
        }));

        if (filterCondition.type === "MultiSelect") {
          predicates.push({
            isComplex: true,
            ignoreCase: true,
            ignoreAccent: false,
            condition: "or",
            predicates: enhancedPredicates,
          });
        } else {
          predicates.push(...enhancedPredicates);
        }
      });

      if (predicates.length > 0) {
        whereClause = {
          isComplex: true,
          ignoreCase: true,
          ignoreAccent: false,
          condition: "and",
          predicates: predicates,
        };
      }

      if (callForData) {
        setFilterPredicates(whereClause);
      }

      return whereClause;
    };

    const getTempPredicateArray = (filterText, filterOption, filterType, columnId) => {
      if (!columnId) {
        return [];
      }
      const option = filterOption || DEFAULT_FILTER_OPTIONS[filterType]?.option || "equal";

      if (TEXT_FIELD_DISABLED_OPTIONS.includes(filterOption)) {
        let predicateCondition;
        if (filterOption === "null" || filterOption === "empty") {
          predicateCondition = "isempty";
        } else if (filterOption === "notnull" || filterOption === "notempty") {
          predicateCondition = "isnotempty";
        }
        return [
          {
            field: columnId,
            operator: predicateCondition,
          },
        ];
      }

      if (filterType === "Date") {
        if (option === "between") {
          if (!filterText || (!filterText.startDate && !filterText.endDate)) {
            return [
              {
                field: columnId,
                operator: "greaterthanorequal",
                value: dayjs().subtract(1, "day").format(),
              },
              {
                field: columnId,
                operator: "lessthanorequal",
                value: dayjs().subtract(1, "day").format(),
              },
            ];
          }
          const predicates = [];
          if (filterText.startDate) {
            const startDateStr = dayjs(filterText.startDate).startOf("day").format();
            predicates.push({
              field: columnId,
              operator: "greaterthanorequal",
              value: startDateStr,
            });
          }

          if (filterText.endDate) {
            const endDateStr = dayjs(filterText.endDate).endOf("day").format();
            predicates.push({
              field: columnId,
              operator: "lessthanorequal",
              value: endDateStr,
            });
          }

          return predicates.length
            ? predicates
            : [
                {
                  field: columnId,
                  operator: "equal",
                  value: dayjs().format(),
                },
              ];
        }
        if (option === "equal") {
          if (!filterText || !filterText.date) {
            const defaultStartDate = dayjs().startOf("day").format();
            const defaultEndDate = dayjs().endOf("day").format();
            return [
              {
                field: columnId,
                operator: "greaterthanorequal",
                value: defaultStartDate,
              },
              {
                field: columnId,
                operator: "lessthanorequal",
                value: defaultEndDate,
              },
            ];
          }

          const dayjsObject = dayjs(filterText.date);
          const startDate = dayjsObject.startOf("day").format();
          const endDate = dayjsObject.endOf("day").format();

          return [
            {
              field: columnId,
              operator: "greaterthanorequal",
              value: startDate,
            },
            {
              field: columnId,
              operator: "lessthanorequal",
              value: endDate,
            },
          ];
        }
        if (!filterText || !filterText.date) {
          return [
            {
              field: columnId,
              operator: option,
              value: dayjs().format(),
            },
          ];
        }
        const filterValue =
          filterText.date && dayjs(filterText.date).isValid()
            ? dayjs.isDayjs(filterText.date)
              ? filterText.date.format()
              : dayjs(filterText.date).format()
            : dayjs().format();

        return [
          {
            field: columnId,
            operator: option,
            value: filterValue,
          },
        ];
      }
      if (filterType === "MultiSelect") {
        const selectedValues = Array.isArray(tempFilterText) ? tempFilterText : [];
        if (selectedValues.length === 0) {
          return [];
        }
        return selectedValues.map((value) => ({
          field: currentColumnId,
          operator: "equal",
          value: value,
        }));
      }

      const defaultValue = filterType === "Number" ? 0 : "";
      const filterValue =
        filterText !== undefined ? (filterType == "Number" ? Number(filterText) : filterText) : defaultValue;

      return [
        {
          field: columnId,
          operator: option,
          value: filterValue,
        },
      ];
    };

    const handleSearchInMultiSelectOptions = (searchText) => {
      setMultiSelectSearchValue(searchText);
      if (!searchText) {
        setMultiSelectFilteredOptions(options);
      } else {
        const filtered = options.filter(
          (option) => option !== "No Records Found" && option.toLowerCase().includes(searchText.toLowerCase()),
        );
        setMultiSelectFilteredOptions(filtered.length > 0 ? filtered : ["No Records Found"]);
      }
    };

    const handleApplyFilter = (clearFilter = false) => {
      if (clearFilter) {
        const button = "mdi mdi-filter-variant text-slate-900";
        const updatedColumns = gridRef.current.slickGrid.getColumns().map((col) => {
          if (col.field === currentColumnId) {
            col.header.buttons[0].cssClass = button;
            col.filterApplied = false;
          }
          return col;
        });
        const updatedFilterConditions = { ...filterConditions };
        delete updatedFilterConditions[currentColumnId];

        setFilterConditions(updatedFilterConditions);

        if (!Object.keys(updatedFilterConditions).length) {
          setFilterPredicates("");
        }

        gridRef.current.slickGrid.setColumns(updatedColumns);
        setAppliedFilters((prev) => prev.filter((data) => data !== currentColumnId));
        if (Object.keys(updatedFilterConditions).length && remoteDataSource) {
          getFilterPredicates(updatedFilterConditions, true);
        }
      } else {
        const predicateArray = getTempPredicateArray(tempFilterText, tempFilterOption, filterType, currentColumnId);
        if (currentColumnId) {
          const newFilterConditions = {
            ...filterConditions,
            [currentColumnId]: {
              ...filterConditions[currentColumnId],
              option: tempFilterOption,
              text: tempFilterText,
              predicate: predicateArray,
            },
          };
          setFilterConditions(newFilterConditions);
          setAppliedFilters((prev) => {
            const data = Array.isArray(prev) ? [...prev, currentColumnId] : [currentColumnId];
            return [...new Set(data)];
          });

          // Notify parent about filter conditions change
          if (onFilterConditionsChange) {
            onFilterConditionsChange(Object.keys(newFilterConditions), newFilterConditions);
          }
        }
        const button = "mdi mdi-filter-variant text-[var(--primary-gradient-color1)]";
        const updatedColumns = gridRef.current.slickGrid.getColumns().map((col) => {
          if (col.field === currentColumnId) {
            col.header.buttons[0].cssClass = button;
            col.filterApplied = true;
          }
          return col;
        });

        gridRef.current.slickGrid.setColumns(updatedColumns);
        if (Object.keys(filterConditions).length && remoteDataSource) {
          getFilterPredicates(
            {
              ...filterConditions,
              [currentColumnId]: {
                ...filterConditions[currentColumnId],
                option: tempFilterOption,
                text: tempFilterText,
                predicate: predicateArray,
              },
            },
            true,
          );
        }
      }

      setAnchorEl(null);
      setOptions([]);
      setOptionsOpen(false);
      setTempFilterText("");
      setTempFilterOption("");
      setCurrentColumnId("");
    };

    const getDateValue = (dateValue, dateType) => {
      let modifiedDate = dateValue;
      if (dateType === "lessthan") {
        modifiedDate = dateValue.startOf("day");
      } else if (dateType === "lessthanorequal") {
        modifiedDate = dateValue.endOf("day");
      } else if (dateType === "greaterthan") {
        modifiedDate = dateValue.endOf("day");
      } else if (dateType === "greaterthanorequal") {
        modifiedDate = dateValue.startOf("day");
      }
      return modifiedDate;
    };

    const handleFilterChange = (event) => {
      const { value } = event.target;
      setTempFilterOption(value);
      const filterValue = tempFilterText.date ? tempFilterText.date : dayjs();

      if (filterType == "Date") {
        if (value === "between") {
          setTempFilterText({
            startDate: dayjs(),
            endDate: dayjs(),
          });
        } else if (value === "equal") {
          setTempFilterText({ date: dayjs() });
        } else {
          setTempFilterText({ date: getDateValue(filterValue, value) });
        }
      }
    };

    const handleInputChange = (_, newInputValue) => {
      setOptionsOpen(!!newInputValue && newInputValue !== tempFilterText);
      setTempFilterText(newInputValue);
    };

    const handleDateChange = (newDate) => {
      setOptionsOpen(false);
      if (newDate) {
        const dayjsObject = dayjs(newDate);
        setTempFilterText({ date: getDateValue(dayjsObject, tempFilterOption) });
      }
    };

    const handleBetweenStartDateChange = (startDate) => {
      setOptionsOpen(false);

      if (startDate) {
        const dayjsObject = dayjs(startDate);
        setTempFilterText((prev) => ({
          ...prev,
          startDate: dayjsObject,
        }));
      }
    };

    const handleBetweenEndDateChange = (endDate) => {
      setOptionsOpen(false);

      if (endDate) {
        const dayjsObject = dayjs(endDate);
        setTempFilterText((prev) => ({
          ...prev,
          endDate: dayjsObject,
        }));
      }
    };

    const fetchOptions = async (text, field, filterOption) => {
      if (!text && tempFilterOption != "equal" && filterType !== "MultiSelect") return;
      setLoading(true);
      let filteredData;

      if (filterType === "MultiSelect") {
        if (remoteDataSource && query) {
          const modifiedQuery = { ...query, skip: 0, take: 1000, requiresCounts: true };
          const dataForOptions = await axiosWrapper.post(remoteDataSource, { value: modifiedQuery });
          filteredData = beforeDataBound
            ? beforeDataBound(dataForOptions)
            : Array.isArray(dataForOptions.result)
              ? dataForOptions.result
              : dataForOptions.data?.result;
        } else if (localData) {
          filteredData = datasetForFetchingOptions;
        }
      } else {
        if (remoteDataSource && query) {
          const filterConditionsWithCurrentConditionUpdated = {
            ...filterConditions,
          };
          filterConditionsWithCurrentConditionUpdated[currentColumnId].text = tempFilterText;
          filterConditionsWithCurrentConditionUpdated[currentColumnId].predicate = [
            {
              field: currentColumnId,
              operator: filterOption,
              value: tempFilterText,
            },
          ];

          const updatedFilterConditions = getUpdatedFilterConditions(filterConditionsWithCurrentConditionUpdated);
          const predicate = getFilterPredicates(updatedFilterConditions, false);
          let where;
          const modifiedQuery = { ...query, skip: 0, take: 20, requiresCounts: true };
          if (query?.where?.length) {
            where = [...query.where];
            where.push(predicate);
          } else {
            where = [predicate];
          }
          modifiedQuery.where = where;
          const dataForOptions = await axiosWrapper.post(remoteDataSource, { value: modifiedQuery });
          filteredData = beforeDataBound
            ? beforeDataBound(dataForOptions)
            : Array.isArray(dataForOptions.result)
              ? dataForOptions.result
              : dataForOptions.data?.result;
        } else if (localData) {
          filteredData = applyFilter(datasetForFetchingOptions, currentColumnId, filterOption, text, filterType);
        }
      }
      const valueGetter = columnDefinitions.find((col) => col.field === currentColumnId)?._valueGetter;

      if (filterType === "MultiSelect") {
        let allValues = [];
        filteredData?.forEach((data) => {
          const fieldValue = valueGetter ? valueGetter(data) : get(data, field);
          if (Array.isArray(fieldValue)) {
            allValues = [...allValues, ...fieldValue.map((item) => item?.name || item)];
          } else if (fieldValue) {
            allValues.push(fieldValue?.name || fieldValue);
          }
        });
        let uniqueOptions = [...new Set(allValues.filter(Boolean))];
        uniqueOptions = uniqueOptions.length ? uniqueOptions.sort() : ["No Records Found"];
        setOptions(uniqueOptions);
        setMultiSelectFilteredOptions(uniqueOptions);
      } else {
        let options = [
          ...new Set(filteredData?.map((data) => (valueGetter ? valueGetter(data) : get(data, field)?.toString()))),
        ];
        options = options.length ? options : ["No Records Found"];
        setOptions(options);
      }
      setLoading(false);
    };

    const getFilterOptions = () => {
      return FILTER_MAPPER[filterType].map((selectedOption) => (
        <MenuItem key={selectedOption} value={`${selectedOption.toLowerCase().replace(/\s+/g, "")}`}>
          {selectedOption}
        </MenuItem>
      ));
    };

    return (
      <div>
        {filterType && (
          <Popover
            id={popoverId}
            open={Boolean(anchorEl)}
            anchorReference="anchorPosition"
            anchorPosition={anchorEl || { top: 0, left: 0 }}
            onClose={handleClosePopover}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            sx={{
              "& .MuiPaper-root": {
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                borderRadius: "8px",
                padding: "20px",
                width: `${filterType === "Date" && tempFilterOption === "between" ? "360px" : "250px"}`,
                marginTop: "10px",
                marginInlineStart: "10px",
              },
            }}
          >
            <div>
              <FormControl fullWidth>
                <Select
                  value={tempFilterOption}
                  displayEmpty
                  onChange={handleFilterChange}
                  style={{ marginBottom: "16px" }}
                >
                  {filterType && getFilterOptions()}
                </Select>
              </FormControl>

              {(filterType === "Text" || filterType == "Number") && (
                <Autocomplete
                  freeSolo
                  open={optionsOpen}
                  options={options}
                  onClose={handleCloseOptions}
                  getOptionDisabled={(option) => option === "No Records Found"}
                  getOptionLabel={(option) => (typeof option === "string" ? option : "")}
                  value={tempFilterText}
                  filterOptions={(x) => x}
                  onInputChange={handleInputChange}
                  // onChange={() => setOptionsOpen(false)}
                  disabled={TEXT_FIELD_DISABLED_OPTIONS.includes(tempFilterOption)}
                  renderOption={(props, option) => (
                    <li {...props} style={{ minWidth: "200px" }}>
                      {option || <em>(empty)</em>}
                    </li>
                  )}
                  loading={loading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      id={`${
                        TEXT_FIELD_DISABLED_OPTIONS.includes(tempFilterOption)
                          ? "filled-disabled"
                          : "outlined-helperText"
                      }`}
                      disabled={TEXT_FIELD_DISABLED_OPTIONS.includes(tempFilterOption)}
                      placeholder={t("Enter the Value")}
                      type={filterType === "Number" ? "number" : "text"}
                      onClick={() => {
                        if (tempFilterOption === "equal") {
                          setOptionsOpen(true);
                        }
                      }}
                    />
                  )}
                />
              )}

              {filterType === "MultiSelect" && (
                <Autocomplete
                  multiple
                  open={optionsOpen}
                  options={multiSelectFilteredOptions.length > 0 ? multiSelectFilteredOptions : options}
                  onClose={handleCloseOptions}
                  getOptionDisabled={(option) => option === "No Records Found"}
                  getOptionLabel={(option) => (typeof option === "string" ? option : "")}
                  value={Array.isArray(tempFilterText) ? tempFilterText : []}
                  filterOptions={(x) => x}
                  inputValue={multiSelectSearchValue}
                  onInputChange={(_, newInputValue, reason) => {
                    if (reason === "input") {
                      handleSearchInMultiSelectOptions(newInputValue);
                      setOptionsOpen(true);
                    }
                  }}
                  onChange={(_, newValue) => {
                    setTempFilterText(newValue.filter((val) => val !== "No Records Found"));
                    setMultiSelectSearchValue("");
                    setMultiSelectFilteredOptions(options);
                  }}
                  disableCloseOnSelect
                  renderOption={(props, option, { selected }) => (
                    <li {...props} style={{ minWidth: "200px" }}>
                      <Checkbox
                        style={{ margimarginInlineEndnRight: 8 }}
                        checked={selected}
                        disabled={option === "No Records Found"}
                      />
                      {option || <em>(empty)</em>}
                    </li>
                  )}
                  loading={loading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={t("Enter the value")}
                      onClick={() => {
                        setOptionsOpen(true);
                        if (multiSelectFilteredOptions.length === 0 && options.length > 0) {
                          setMultiSelectFilteredOptions(options);
                        }
                      }}
                    />
                  )}
                />
              )}

              {filterType === "Date" && (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <div style={{ display: "flex", gap: "16px" }}>
                    <DatePicker
                      disabled={TEXT_FIELD_DISABLED_OPTIONS.includes(tempFilterOption)}
                      onChange={tempFilterOption === "between" ? handleBetweenStartDateChange : handleDateChange}
                      value={
                        tempFilterOption === "between"
                          ? tempFilterText.startDate || dayjs()
                          : tempFilterText.date || dayjs()
                      }
                    />
                    {tempFilterOption === "between" && (
                      <DatePicker onChange={handleBetweenEndDateChange} value={tempFilterText.endDate || dayjs()} />
                    )}
                  </div>
                </LocalizationProvider>
              )}

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "10px",
                  paddingTop: "16px",
                }}
              >
                <ButtonComponent
                  buttonType="gradientButton"
                  style={{ flex: "1", height: "35px", borderRadius: "8px" }}
                  onClick={() => handleApplyFilter(false)}
                >
                  {t("Apply")}
                </ButtonComponent>
                <ButtonComponent
                  buttonType="borderButton"
                  style={{ flex: "1", height: "35px", borderRadius: "8px" }}
                  onClick={() => handleApplyFilter(true)}
                >
                  {t("Clear")}
                </ButtonComponent>
              </div>
            </div>
          </Popover>
        )}
      </div>
    );
  },
);
FilterComponent.displayName = "FilterComponent";
FilterComponent.propTypes = {
  filterConditions: PropTypes.object.isRequired,
  setFilterConditions: PropTypes.func.isRequired,
  getUpdatedFilterConditions: PropTypes.func.isRequired,
  applyFilter: PropTypes.func.isRequired,
};

export default FilterComponent;
